import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  X, XCircle, CheckCircle2, ExternalLink, ArrowLeft, ArrowRight,
  FileText, GraduationCap, CalendarDays, ScrollText, Handshake,
  Target, Plane, Wrench, Clipboard, ChevronDown, Undo2,
} from 'lucide-react';
import PageShell from '../components/layout/PageShell';

const API_URL = process.env.REACT_APP_API_URL;

// Outlined button that fills with its colour on hover so the admin can tell which action they're about to take.
const DecisionButton = ({ color, hoverBg, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        fontSize: '12px', fontWeight: '600', padding: '6px 14px', borderRadius: '6px',
        cursor: 'pointer', transition: 'all 0.15s ease',
        color: hover ? '#fff' : color,
        background: hover ? hoverBg : '#fff',
        border: `1px solid ${hoverBg}`,
        boxShadow: hover ? '0 2px 6px rgba(0,0,0,0.18)' : 'none',
        transform: hover ? 'translateY(-1px)' : 'none',
      }}
    >{children}</button>
  );
};

// "More actions" dropdown for already-decided registrations. Offers to reverse the decision,
// which emails the registrant an apology plus the corrected outcome. Menu is position:fixed
// so the table's overflow doesn't clip it.
const MoreActionsMenu = ({ status, onRevert }) => {
  const [pos, setPos] = useState(null);
  const [hover, setHover] = useState(false);
  const [itemHover, setItemHover] = useState(false);
  const toConfirmed = status === 'Declined';
  const accent = toConfirmed ? '#2f9e44' : '#c92a2a';
  return (
    <>
      <button
        onClick={ev => { const r = ev.currentTarget.getBoundingClientRect(); setPos(pos ? null : { top: r.bottom + 4, right: window.innerWidth - r.right }); }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap', color: '#444', background: hover || pos ? '#f1f3f5' : '#fff', border: '1px solid #ccc', transition: 'all 0.15s ease' }}
      >More actions <ChevronDown size={13} /></button>
      {pos && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setPos(null)} />
          <div style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 1000, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,0.15)', padding: '4px', minWidth: '230px' }}>
            <button
              onMouseEnter={() => setItemHover(true)}
              onMouseLeave={() => setItemHover(false)}
              onClick={() => { setPos(null); onRevert(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', fontSize: '12px', fontWeight: '600', padding: '9px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', color: itemHover ? '#fff' : accent, background: itemHover ? accent : 'transparent', transition: 'all 0.15s ease' }}
            >
              <Undo2 size={13} /> {toConfirmed ? 'Revert to Confirmed' : 'Revert to Declined'}
            </button>
          </div>
        </>
      )}
    </>
  );
};

// Hover text for a registration's status tag: who accepted / declined it.
const decisionTitle = (r) => {
  if (!['Confirmed', 'Declined'].includes(r.status)) return undefined;
  const verb = r.status === 'Confirmed' ? 'Accepted' : 'Declined';
  const when = r.decidedAt ? ' on ' + new Date(r.decidedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';
  return r.decidedByName ? `${verb} by ${r.decidedByName}${when}` : `${verb} (admin not recorded)`;
};

const pickDecision = (reg) => ({ status: reg.status, decidedByName: reg.decidedByName, decidedByEmail: reg.decidedByEmail, decidedAt: reg.decidedAt });

const STATUS_COLORS = {
  'Submitted':   { color: '#e67700', bg: '#fff9db', border: '#ffe066' },
  'In Review':   { color: '#1971c2', bg: '#e8f4fd', border: '#a5d8ff' },
  'In Progress': { color: '#2f9e44', bg: '#ebfbee', border: '#b2f2bb' },
  'Completed':   { color: '#5f3dc4', bg: '#f3f0ff', border: '#d0bfff' },
  'Cancelled':   { color: '#c92a2a', bg: '#fff5f5', border: '#ffc9c9' },
};

const STATUSES = ['Submitted', 'In Review', 'In Progress', 'Completed', 'Cancelled'];

// Mirrors routes/workshops.js's isWorkshopExpired: expired once the end time
// (or start time, if no end was set) is in the past. Legacy free-text dates
// that don't parse fail open (treated as not expired).
function isWorkshopExpired(workshop) {
  if (!workshop) return false;
  const raw = workshop.endDate || workshop.date;
  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) return false;
  return parsed.getTime() < Date.now();
}

// Same categorization options as the Materials page's upload form, so a
// fulfilled request shows up there with consistent filtering.
const FULFILL_OPTIONS = {
  type:     ['Deck', 'One-pager', 'Case Study', 'Architecture Diagram', 'Workshop'],
  product:  ['Okta WIC', 'Okta CIS', 'Auth0'],
  industry: ['Financial Services', 'Healthcare', 'Retail', 'Technology', 'Government'],
};

const TYPE_ICON = {
  'Pre-Sales': Target, 'In-Flight Deal': Plane, 'Sold Deal': CheckCircle2, 'Break-Fix/Post-Sales': Wrench,
};

const s = {
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '28px' },
  statCard: (accent) => ({ background: '#fff', border: `1px solid ${accent}22`, borderTop: `3px solid ${accent}`, borderRadius: '8px', padding: '16px', textAlign: 'center' }),
  statNum: { fontSize: '26px', fontWeight: '700', marginBottom: '4px', color: '#111' },
  statLabel: { fontSize: '11px', color: '#888', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.05em' },
  tabs: { display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #eee' },
  tab: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '10px 20px', fontSize: '13px', fontWeight: active ? '700' : '500',
    color: active ? '#fd5f0d' : '#888', cursor: 'pointer', background: 'none',
    border: 'none', borderBottom: active ? '2px solid #fd5f0d' : '2px solid transparent',
    marginBottom: '-2px',
  }),
  toolbar: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' },
  filterBtn: (active) => ({
    padding: '5px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
    fontWeight: active ? '600' : '400',
    border: `1px solid ${active ? '#fd5f0d' : '#ddd'}`,
    background: active ? '#fef0e6' : '#fff', color: active ? '#fd5f0d' : '#555',
  }),
  searchInput: { marginLeft: 'auto', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', width: '200px' },
  tableWrap: { overflowX: 'auto', background: '#fff', border: '1px solid #eee', borderRadius: '8px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid #e5e5e5', fontWeight: '600', fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid #f5f5f5', verticalAlign: 'middle' },
  badge: (status) => { const c = STATUS_COLORS[status] || STATUS_COLORS['Submitted']; return { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', color: c.color, background: c.bg, border: `1px solid ${c.border}` }; },
  actionBtn: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#fd5f0d', background: 'none', border: 'none', cursor: 'pointer', padding: '0', marginRight: '12px', fontWeight: '600' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #f0f0f0', fontSize: '12px', color: '#888' },
  pageBtn: (disabled) => ({ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', border: '1px solid #ddd', background: disabled ? '#fafafa' : '#fff', color: disabled ? '#ccc' : '#333', cursor: disabled ? 'not-allowed' : 'pointer' }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '580px', width: '100%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' },
  modalClose: { position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex' },
  modalIcon: { color: '#555', marginBottom: '2px' },
  modalTitle: { fontSize: '16px', fontWeight: '700', marginBottom: '2px' },
  modalSub: { fontSize: '12px', color: '#888', marginBottom: '20px' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' },
  fieldLabel: { fontSize: '10px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' },
  fieldValue: { fontSize: '13px', color: '#222' },
  divider: { borderTop: '1px solid #eee', margin: '16px 0' },
  statusSelect: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', background: '#fff' },
  saveBtn: { padding: '6px 16px', background: '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: '8px' },
  errorBanner: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', background: '#fdecea', border: '1px solid #f5c2be', borderRadius: '6px', color: '#b00020', fontSize: '13px', marginBottom: '16px' },
  successBanner: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', background: '#ebfbee', border: '1px solid #b2f2bb', borderRadius: '6px', color: '#2f9e44', fontSize: '13px', marginBottom: '16px' },
  loading: { textAlign: 'center', padding: '40px', color: '#888', fontSize: '14px' },
  emptyRow: { textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '13px' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' },
  uploadBox: { border: '2px dashed #ddd', borderRadius: '8px', padding: '18px', textAlign: 'center', marginBottom: '10px', cursor: 'pointer', background: '#fafafa' },
  fileInfo: { fontSize: '12px', color: '#666', marginTop: '6px' },
  materialLink: { fontSize: '13px', color: '#fd5f0d', fontWeight: '600', textDecoration: 'none' },
};

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div style={s.fieldLabel}>{label}</div>
      <div style={s.fieldValue}>{value}</div>
    </div>
  );
}

// ── Engagement detail modal ──
function EngagementModal({ item, token, onClose, onStatusChange }) {
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}api/admin/engagements/${item._id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed.');
      onStatusChange(item._id, status);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
          {(() => { const TypeIcon = TYPE_ICON[item.engagementType] || Clipboard; return <TypeIcon size={22} strokeWidth={1.75} style={s.modalIcon} />; })()}
          <div style={s.modalTitle}>{item.accountName}</div>
        </div>
        <div style={s.modalSub}>
          #{item.planeSequenceId || '—'} · {item.requesterName || item.requesterEmail} · {new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        {error && <div style={s.errorBanner}><XCircle size={14} /> {error}</div>}
        <div style={s.fieldGrid}>
          <Field label="Requester Email" value={item.requesterEmail} />
          <Field label="Engagement Type" value={item.engagementType} />
          <Field label="Product" value={item.product} />
          <Field label="Use Case" value={item.useCase} />
          <Field label="RAAH Product" value={item.raahProduct} />
          <Field label="Urgency" value={item.urgency} />
          <Field label="Timeline" value={item.timeline} />
          <Field label="Stakeholders" value={item.stakeholders} />
        </div>
        <Field label="Opportunity Summary" value={item.opportunitySummary} />
        {item.notes && <><div style={{ height: '12px' }} /><Field label="Additional Notes" value={item.notes} /></>}
        <div style={s.divider} />
        <div style={s.fieldLabel}>Update Status</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
          <select style={s.statusSelect} value={status} onChange={e => setStatus(e.target.value)}>
            {STATUSES.map(st => <option key={st}>{st}</option>)}
          </select>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          {item.planeIssueUrl && <a href={item.planeIssueUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#fd5f0d', marginLeft: 'auto' }}><ExternalLink size={12} /> Plane #{item.planeSequenceId}</a>}
        </div>
      </div>
    </div>
  );
}

// ── Material request detail modal ──
function MaterialRequestModal({ item, token, onClose, onStatusChange, onFulfilled }) {
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [fulfilledMaterial, setFulfilledMaterial] = useState(item.fulfilledMaterialId || null);
  const [fulfillFile, setFulfillFile] = useState(null);
  const [fulfillTitle, setFulfillTitle] = useState(`${item.assetType} — ${item.customerContext}`);
  const [fulfillType, setFulfillType] = useState('');
  const [fulfillProduct, setFulfillProduct] = useState('');
  const [fulfillIndustry, setFulfillIndustry] = useState('');
  const [fulfilling, setFulfilling] = useState(false);
  const [fulfillFeedback, setFulfillFeedback] = useState({ type: '', msg: '' });
  const fileInputRef = React.useRef();

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}api/admin/material-requests/${item._id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed.');
      onStatusChange(item._id, status);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleFulfill = async (e) => {
    e.preventDefault();
    if (!fulfillFile || !fulfillTitle) return;
    setFulfilling(true);
    setFulfillFeedback({ type: '', msg: '' });
    try {
      const formData = new FormData();
      formData.append('file', fulfillFile);
      formData.append('title', fulfillTitle);
      formData.append('type', fulfillType);
      formData.append('product', fulfillProduct);
      formData.append('industry', fulfillIndustry);

      const res = await fetch(`${API_URL}api/materials/request/${item._id}/fulfill`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fulfill request.');

      setFulfilledMaterial(data.material);
      setStatus('Completed');
      onStatusChange(item._id, 'Completed');
      onFulfilled(item._id, data.material);
      setFulfillFeedback({
        type: 'success',
        msg: data.emailSent
          ? `"${data.material.title}" sent — the requester has been emailed.`
          : `"${data.material.title}" saved, but the notification email failed to send.`,
      });
      setFulfillFile(null);
    } catch (err) {
      setFulfillFeedback({ type: 'error', msg: err.message });
    } finally {
      setFulfilling(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <FileText size={22} strokeWidth={1.75} style={s.modalIcon} />
        <div style={s.modalTitle}>{item.assetType}</div>
        <div style={s.modalSub}>
          {item.requesterName || item.requesterEmail} · {new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        {error && <div style={s.errorBanner}><XCircle size={14} /> {error}</div>}
        <div style={s.fieldGrid}>
          <Field label="Requester Email" value={item.requesterEmail} />
          <Field label="Asset Type" value={item.assetType} />
          <Field label="Deadline" value={item.deadline} />
          <Field label="Use Case" value={item.useCase} />
        </div>
        <Field label="Customer / Context" value={item.customerContext} />
        <div style={s.divider} />
        <div style={s.fieldLabel}>Update Status</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
          <select style={s.statusSelect} value={status} onChange={e => setStatus(e.target.value)}>
            {STATUSES.map(st => <option key={st}>{st}</option>)}
          </select>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          {item.planeIssueUrl && <a href={item.planeIssueUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#fd5f0d', marginLeft: 'auto' }}><ExternalLink size={12} /> Plane #{item.planeSequenceId}</a>}
        </div>

        <div style={s.divider} />
        <div style={s.fieldLabel}>Respond With a File</div>

        {fulfilledMaterial && (
          <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '13px' }}>
            Fulfilled with{' '}
            <a href={fulfilledMaterial.downloadUrl} target="_blank" rel="noreferrer" style={s.materialLink}>
              {fulfilledMaterial.title}
            </a>
            <span style={{ color: '#888' }}> — uploading a new file below will replace it and re-notify the requester.</span>
          </div>
        )}

        {fulfillFeedback.msg && (
          <div style={{ marginTop: '10px', ...(fulfillFeedback.type === 'success' ? s.successBanner : s.errorBanner) }}>
            {fulfillFeedback.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {fulfillFeedback.msg}
          </div>
        )}

        <form onSubmit={handleFulfill} style={{ marginTop: '10px' }}>
          <div
            style={{ ...s.uploadBox, borderColor: fulfillFile ? '#fd5f0d' : '#ddd' }}
            onClick={() => fileInputRef.current.click()}
          >
            {fulfillFile ? (
              <>
                <div style={{ fontWeight: '600' }}>{fulfillFile.name}</div>
                <div style={s.fileInfo}>{(fulfillFile.size / 1024 / 1024).toFixed(2)} MB</div>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: '#888' }}>Click to select a file (max 50MB)</div>
            )}
            <input ref={fileInputRef} type="file" style={{ display: 'none' }}
              onChange={e => setFulfillFile(e.target.files[0] || null)} />
          </div>

          <input style={s.input} type="text" placeholder="Document title" value={fulfillTitle} onChange={e => setFulfillTitle(e.target.value)} required />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
            <select style={s.input} value={fulfillType} onChange={e => setFulfillType(e.target.value)}>
              <option value="">Type — Select</option>
              {FULFILL_OPTIONS.type.map(o => <option key={o}>{o}</option>)}
            </select>
            <select style={s.input} value={fulfillProduct} onChange={e => setFulfillProduct(e.target.value)}>
              <option value="">Product — Select</option>
              {FULFILL_OPTIONS.product.map(o => <option key={o}>{o}</option>)}
            </select>
            <select style={s.input} value={fulfillIndustry} onChange={e => setFulfillIndustry(e.target.value)}>
              <option value="">Industry — Select</option>
              {FULFILL_OPTIONS.industry.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>

          <button type="submit" style={s.saveBtn} disabled={fulfilling || !fulfillFile} >
            {fulfilling ? 'Sending…' : fulfilledMaterial ? 'Replace & Re-send' : 'Send & Complete Request'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shared paginated table ──
function RequestTable({ columns, rows, loading, page, pagination, onPage, onSelect, renderRow }) {
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>{columns.map(c => <th key={c} style={s.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {loading
            ? <tr><td colSpan={columns.length} style={s.loading}>Loading…</td></tr>
            : rows.length === 0
              ? <tr><td colSpan={columns.length} style={s.emptyRow}>No requests found.</td></tr>
              : rows.map(r => renderRow(r))}
        </tbody>
      </table>
      <div style={s.pagination}>
        <span>
          {pagination.total === 0 ? 'No results'
            : `Showing ${(page - 1) * 15 + 1}–${Math.min(page * 15, pagination.total)} of ${pagination.total}`}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={s.pageBtn(page <= 1)} disabled={page <= 1} onClick={() => onPage(page - 1)}><ArrowLeft size={12} /> Prev</button>
          <button style={s.pageBtn(page >= pagination.pages)} disabled={page >= pagination.pages} onClick={() => onPage(page + 1)}>Next <ArrowRight size={12} /></button>
        </div>
      </div>
    </div>
  );
}


// ── Page colours per route ────────────────────────────────────────────────────
const PAGE_COLORS = [
  '#0057ff','#f76707','#2f9e44','#5f3dc4','#1971c2',
  '#c2255c','#e67700','#0ca678','#868e96','#495057','#212529',
];

function VisitsChart({ data, loading, days, onDaysChange }) {
  const [tooltip, setTooltip] = React.useState(null); // { x, y, date, items }

  const cs = {
    wrap:     { background: '#fff', border: '1px solid #eee', borderRadius: '10px', padding: '20px 24px', marginBottom: '28px' },
    header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:    { fontSize: '14px', fontWeight: '700', color: '#111' },
    sub:      { fontSize: '12px', color: '#888', marginTop: '2px' },
    controls: { display: 'flex', gap: '6px' },
    dayBtn:   (active) => ({ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: active ? '700' : '400', border: `1px solid ${active ? '#fd5f0d' : '#ddd'}`, background: active ? '#fef0e6' : '#fff', color: active ? '#fd5f0d' : '#555' }),
    topPages: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f0f0f0' },
    topCard:  { background: '#f8f9fb', borderRadius: '8px', padding: '12px' },
    topNum:   { fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '2px' },
    topLabel: { fontSize: '11px', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' },
    legend:   { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '14px' },
    legendItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#555' },
    dot:      (c) => ({ width: '8px', height: '8px', borderRadius: '50%', background: c, flexShrink: 0 }),
    empty:    { textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '13px' },
    loading:  { textAlign: 'center', padding: '40px', color: '#888', fontSize: '13px' },
    tooltip:  { position: 'fixed', background: '#fff', border: '1px solid #eee', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', pointerEvents: 'none', zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: '140px' },
  };

  // Build chart data
  const { chartData, pages, totalVisits } = React.useMemo(() => {
    if (!data?.results) return { chartData: [], pages: [], totalVisits: 0 };
    const byDate = {};
    for (const r of data.results) {
      const { date, label } = r._id;
      if (!byDate[date]) byDate[date] = { date };
      byDate[date][label] = (byDate[date][label] || 0) + r.count;
    }
    const chartData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    const pages     = (data.totals || []).map((t, i) => ({ path: t._id.path, label: t._id.label, count: t.count, color: PAGE_COLORS[i % PAGE_COLORS.length] }));
    const totalVisits = pages.reduce((s, p) => s + p.count, 0);
    return { chartData, pages, totalVisits };
  }, [data]);

  if (loading) return <div style={{ ...cs.wrap }}><div style={cs.loading}>Loading visits…</div></div>;

  // SVG dimensions
  const W = 900, H = 200, PAD_L = 36, PAD_R = 8, PAD_T = 8, PAD_B = 28;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const maxVal = Math.max(...chartData.map(d => pages.reduce((s, p) => s + (d[p.label] || 0), 0)), 1);
  const barW   = chartData.length ? Math.max(4, Math.min(40, chartW / chartData.length * 0.7)) : 20;
  const gap    = chartData.length ? chartW / chartData.length : 0;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxVal * f));

  function fmtDate(d) {
    const [, m, day] = d.split('-');
    return `${parseInt(m)}/${parseInt(day)}`;
  }
  function fmtDateFull(d) {
    const [y, m, day] = d.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(day)).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }

  return (
    <div style={cs.wrap}>
      <div style={cs.header}>
        <div>
          <div style={cs.title}>Page Visits</div>
          <div style={cs.sub}>{totalVisits.toLocaleString()} total views · last {days} days</div>
        </div>
        <div style={cs.controls}>
          {[7, 14, 30, 90].map(d => (
            <button key={d} style={cs.dayBtn(days === d)} onClick={() => onDaysChange(d)}>{d}d</button>
          ))}
        </div>
      </div>

      {chartData.length === 0 ? (
        <div style={cs.empty}>No page view data yet. Views will appear here once users navigate the portal.</div>
      ) : (
        <>
          {/* SVG Chart */}
          <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}
              onMouseLeave={() => setTooltip(null)}>

              {/* Y grid lines + labels */}
              {yTicks.map(v => {
                const y = PAD_T + chartH - (v / maxVal) * chartH;
                return (
                  <g key={v}>
                    <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#f0f0f0" strokeWidth="1" />
                    <text x={PAD_L - 4} y={y + 4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>
                  </g>
                );
              })}

              {/* Stacked bars */}
              {chartData.map((d, i) => {
                const cx    = PAD_L + i * gap + gap / 2;
                const x     = cx - barW / 2;
                let yBottom = PAD_T + chartH;
                const total = pages.reduce((s, p) => s + (d[p.label] || 0), 0);

                return (
                  <g key={d.date}
                    onMouseEnter={e => {
                      const items = pages.filter(p => d[p.label] > 0).map(p => ({ label: p.label, count: d[p.label], color: p.color }));
                      setTooltip({ x: e.clientX, y: e.clientY, date: fmtDateFull(d.date), items, total });
                    }}
                    onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {pages.map(p => {
                      const val = d[p.label] || 0;
                      if (!val) return null;
                      const bh = (val / maxVal) * chartH;
                      yBottom -= bh;
                      const isTop = pages.filter(pp => (d[pp.label] || 0) > 0).slice(-1)[0]?.label === p.label;
                      return (
                        <rect key={p.label} x={x} y={yBottom} width={barW} height={bh}
                          fill={p.color} rx={isTop ? 3 : 0} ry={isTop ? 3 : 0} />
                      );
                    })}
                    {/* X label */}
                    <text x={cx} y={H - 6} textAnchor="middle" fontSize="10" fill="#bbb">{fmtDate(d.date)}</text>
                    {/* Hover target */}
                    <rect x={x - 4} y={PAD_T} width={barW + 8} height={chartH} fill="transparent" />
                  </g>
                );
              })}
            </svg>

            {/* Tooltip */}
            {tooltip && (
              <div style={{ ...cs.tooltip, left: tooltip.x + 12, top: tooltip.y - 20 }}>
                <div style={{ fontWeight: '700', marginBottom: '6px', color: '#111' }}>{tooltip.date}</div>
                {tooltip.items.map(item => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                    <div style={cs.dot(item.color)} />
                    <span style={{ flex: 1, color: '#555' }}>{item.label}</span>
                    <span style={{ fontWeight: '600', color: '#111' }}>{item.count}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #eee', marginTop: '6px', paddingTop: '6px', fontWeight: '700', color: '#111', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total</span><span>{tooltip.total}</span>
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div style={cs.legend}>
            {pages.map(p => (
              <div key={p.path} style={cs.legendItem}>
                <div style={cs.dot(p.color)} />
                <span>{p.label}</span>
              </div>
            ))}
          </div>

          {/* Top pages */}
          <div style={cs.topPages}>
            {pages.slice(0, 6).map(p => (
              <div key={p.path} style={cs.topCard}>
                <div style={cs.topNum}>{p.count.toLocaleString()}</div>
                <div style={cs.topLabel}>{p.label}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const [token, setToken] = useState('');
  const [tab, setTab] = useState('engagements');

  // Page views
  const [pvDays, setPvDays]           = useState(7);
  const [pvData, setPvData]           = useState(null);
  const [pvLoading, setPvLoading]     = useState(true);

  // Logs
  const [logs, setLogs]                     = useState([]);
  const [logPagination, setLogPagination]   = useState({ total: 0, page: 1, pages: 1 });
  const [logPage, setLogPage]               = useState(1);
  const [logSearch, setLogSearch]           = useState('');
  const [logType, setLogType]               = useState('');
  const [logRole, setLogRole]               = useState('');
  const [logsLoading, setLogsLoading]       = useState(true);

  // Bookings
  const [bookings, setBookings]           = useState([]);
  const [bookPagination, setBookPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [bookPage, setBookPage]           = useState(1);
  const [bookFilter, setBookFilter]       = useState('All');
  const [bookLoading, setBookLoading]     = useState(true);
  const [bookSelected, setBookSelected]   = useState(null);

  // Workshop registrations
  const [wsRegs, setWsRegs]           = useState([]);
  const [wsPagination, setWsPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [wsPage, setWsPage]           = useState(1);
  const [wsFilter, setWsFilter]       = useState('All');
  const [wsSearch, setWsSearch]       = useState('');
  const [wsLoading, setWsLoading]     = useState(true);
  const [wsSelected, setWsSelected]   = useState(null);
  const [stats, setStats] = useState(null);

  // Engagements
  const [engagements, setEngagements] = useState([]);
  const [engPagination, setEngPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [engPage, setEngPage] = useState(1);
  const [engFilter, setEngFilter] = useState('All');
  const [engSearch, setEngSearch] = useState('');
  const [engLoading, setEngLoading] = useState(true);
  const [engSelected, setEngSelected] = useState(null);

  // Material requests
  const [matRequests, setMatRequests] = useState([]);
  const [matPagination, setMatPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [matPage, setMatPage] = useState(1);
  const [matFilter, setMatFilter] = useState('All');
  const [matSearch, setMatSearch] = useState('');
  const [matLoading, setMatLoading] = useState(true);
  const [matSelected, setMatSelected] = useState(null);

  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (t, page = 1, search = '', resourceType = '', actorRole = '') => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (search)       params.set('actorEmail', search);
      if (resourceType) params.set('resourceType', resourceType);
      if (actorRole)    params.set('actorRole', actorRole);
      const res  = await fetch(`${API_URL}api/logs?${params}`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLogs(data.logs);
      setLogPagination(data.pagination);
    } catch (err) { setError(err.message); }
    finally { setLogsLoading(false); }
  }, []);

  const fetchPageViews = useCallback(async (t, days = 7) => {
    setPvLoading(true);
    try {
      const res  = await fetch(`${API_URL}api/pageviews/summary?days=${days}`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setPvData(data);
    } catch (err) { console.error('PageView fetch error:', err.message); }
    finally { setPvLoading(false); }
  }, []);

  const getToken = useCallback(async () => {
    const t = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
    setToken(t);
    return t;
  }, [getAccessTokenSilently]);

  const fetchStats = useCallback(async (t) => {
    try {
      const res = await fetch(`${API_URL}api/admin/stats`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      setStats(data);
    } catch (err) { console.error('Stats fetch failed', err); }
  }, []);

  const fetchBookings = useCallback(async (t, page = 1, status = 'All') => {
    setBookLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (status !== 'All') params.set('status', status);
      const res  = await fetch(`${API_URL}api/booking?${params}`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setBookings(data.bookings);
      setBookPagination(data.pagination);
    } catch (err) { setError(err.message); }
    finally { setBookLoading(false); }
  }, []);

  const fetchWsRegs = useCallback(async (t, page = 1, status = 'All', email = '') => {
    setWsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (status !== 'All') params.set('status', status);
      const res  = await fetch(`${API_URL}api/admin/workshop-registrations?${params}`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setWsRegs(data.registrations);
      setWsPagination(data.pagination);
    } catch (err) { setError(err.message); }
    finally { setWsLoading(false); }
  }, []);

  const fetchEngagements = useCallback(async (t, page = 1, status = 'All', email = '') => {
    setEngLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (status !== 'All') params.set('status', status);
      if (email) params.set('email', email);
      const res = await fetch(`${API_URL}api/admin/engagements?${params}`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEngagements(data.engagements);
      setEngPagination(data.pagination);
    } catch (err) { setError(err.message); }
    finally { setEngLoading(false); }
  }, []);

  const fetchMatRequests = useCallback(async (t, page = 1, status = 'All', email = '') => {
    setMatLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (status !== 'All') params.set('status', status);
      if (email) params.set('email', email);
      const res = await fetch(`${API_URL}api/admin/material-requests?${params}`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMatRequests(data.requests);
      setMatPagination(data.pagination);
    } catch (err) { setError(err.message); }
    finally { setMatLoading(false); }
  }, []);

  useEffect(() => {
    getToken().then(t => {
      fetchStats(t);
      fetchEngagements(t);
      fetchMatRequests(t);
      fetchWsRegs(t);
      fetchBookings(t);
      fetchLogs(t);
      fetchPageViews(t);
    });
  }, []);

  const statCards = stats ? [
    { label: 'Engagements',        num: stats.engagements.total,                        accent: '#fd5f0d' },
    { label: 'Eng. Submitted',     num: stats.engagements.submitted,                    accent: '#e67700' },
    { label: 'Eng. In Progress',   num: stats.engagements.inProgress,                   accent: '#2f9e44' },
    { label: 'Material Requests',  num: stats.materialRequests.total,                   accent: '#5f3dc4' },
    { label: 'Mat. Submitted',     num: stats.materialRequests.submitted,               accent: '#e67700' },
    { label: 'Files Available',    num: stats.materials.total,                          accent: '#1971c2' },
    { label: 'Total Downloads',    num: stats.materials.totalDownloads,                 accent: '#2f9e44' },
    { label: 'WS Registrations',   num: stats.workshopRegistrations?.total     || 0,   accent: '#f76707' },
    { label: 'WS Pending',         num: stats.workshopRegistrations?.pending    || 0,   accent: '#e67700' },
    { label: 'WS Confirmed',       num: stats.workshopRegistrations?.confirmed  || 0,   accent: '#2f9e44' },
    { label: 'Total Bookings',     num: stats.bookings?.total                   || 0,   accent: '#fd5f0d' },
    { label: 'Upcoming Bookings',  num: stats.bookings?.upcoming                || 0,   accent: '#2f9e44' },
  ] : [];

  return (
    <PageShell title="Admin Console" desc="RAAH internal view — request management and user activity.">

      {/* Stats */}
      {/* ── Visits Chart ── */}
      <VisitsChart
        data={pvData}
        loading={pvLoading}
        days={pvDays}
        onDaysChange={d => { setPvDays(d); getToken().then(t => fetchPageViews(t, d)); }}
      />

      {error && <div style={s.errorBanner}><XCircle size={14} /> {error}</div>}

      {/* Tabs */}
      <div style={s.tabs}>
        <button style={s.tab(tab === 'engagements')} onClick={() => setTab('engagements')}>
          <Handshake size={14} /> Engagements {stats ? `(${stats.engagements.total})` : ''}
        </button>
        <button style={s.tab(tab === 'materials')} onClick={() => setTab('materials')}>
          <FileText size={14} /> Material Requests {stats ? `(${stats.materialRequests.total})` : ''}
        </button>
        <button style={s.tab(tab === 'workshops')} onClick={() => setTab('workshops')}>
          <GraduationCap size={14} /> Workshop Registrations {wsPagination.total > 0 ? `(${wsPagination.total})` : ''}
        </button>
        <button style={s.tab(tab === 'bookings')} onClick={() => setTab('bookings')}>
          <CalendarDays size={14} /> Bookings {bookPagination.total > 0 ? `(${bookPagination.total})` : ''}
        </button>
        <button style={s.tab(tab === 'logs')} onClick={() => setTab('logs')}>
          <ScrollText size={14} /> Audit Log {logPagination.total > 0 ? `(${logPagination.total})` : ''}
        </button>
      </div>

      {/* Engagements tab */}
      {tab === 'engagements' && (
        <>
          <div style={s.toolbar}>
            {['All', ...STATUSES].map(f => (
              <button key={f} style={s.filterBtn(engFilter === f)} onClick={() => { setEngFilter(f); setEngPage(1); fetchEngagements(token, 1, f, engSearch); }}>{f}</button>
            ))}
            <input style={s.searchInput} placeholder="Search by email…" value={engSearch}
              onChange={e => { setEngSearch(e.target.value); setEngPage(1); fetchEngagements(token, 1, engFilter, e.target.value); }} />
          </div>
          <RequestTable
            columns={['#', 'Account', 'Type', 'Product', 'Requester', 'Urgency', 'Status', 'Submitted', 'Actions']}
            rows={engagements} loading={engLoading} page={engPage} pagination={engPagination}
            onPage={p => { setEngPage(p); fetchEngagements(token, p, engFilter, engSearch); }}
            renderRow={e => (
              <tr key={e._id} style={{ cursor: 'pointer' }} onClick={() => setEngSelected(e)}>
                <td style={s.td}><span style={{ color: '#aaa', fontSize: '12px' }}>#{e.planeSequenceId || '—'}</span></td>
                <td style={s.td}><span style={{ fontWeight: '600' }}>{e.accountName}</span></td>
                <td style={s.td}>
                  {e.engagementType ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {(() => { const TypeIcon = TYPE_ICON[e.engagementType]; return TypeIcon ? <TypeIcon size={12} /> : null; })()} {e.engagementType}
                    </span>
                  ) : '—'}
                </td>
                <td style={s.td}>{e.product || '—'}</td>
                <td style={s.td}>
                  <div>{e.requesterName || '—'}</div>
                  <div style={{ fontSize: '11px', color: '#aaa' }}>{e.requesterEmail}</div>
                </td>
                <td style={s.td}>{e.urgency || '—'}</td>
                <td style={s.td}><span style={s.badge(e.status)}>{e.status}</span></td>
                <td style={s.td} onClick={ev => ev.stopPropagation()}>{new Date(e.createdAt).toLocaleDateString()}</td>
                <td style={s.td} onClick={ev => ev.stopPropagation()}>
                  <button style={s.actionBtn} onClick={() => setEngSelected(e)}>View</button>
                  {e.planeIssueUrl && <a href={e.planeIssueUrl} target="_blank" rel="noreferrer" style={s.actionBtn}>Plane</a>}
                </td>
              </tr>
            )}
          />
        </>
      )}

      {/* Material requests tab */}
      {tab === 'materials' && (
        <>
          <div style={s.toolbar}>
            {['All', ...STATUSES].map(f => (
              <button key={f} style={s.filterBtn(matFilter === f)} onClick={() => { setMatFilter(f); setMatPage(1); fetchMatRequests(token, 1, f, matSearch); }}>{f}</button>
            ))}
            <input style={s.searchInput} placeholder="Search by email…" value={matSearch}
              onChange={e => { setMatSearch(e.target.value); setMatPage(1); fetchMatRequests(token, 1, matFilter, e.target.value); }} />
          </div>
          <RequestTable
            columns={['Asset Type', 'Customer / Context', 'Use Case', 'Requester', 'Deadline', 'Status', 'Submitted', 'Actions']}
            rows={matRequests} loading={matLoading} page={matPage} pagination={matPagination}
            onPage={p => { setMatPage(p); fetchMatRequests(token, p, matFilter, matSearch); }}
            renderRow={r => (
              <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => setMatSelected(r)}>
                <td style={s.td}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}><FileText size={13} /> {r.assetType}</span></td>
                <td style={s.td}>{r.customerContext}</td>
                <td style={s.td}>{r.useCase || '—'}</td>
                <td style={s.td}>
                  <div>{r.requesterName || '—'}</div>
                  <div style={{ fontSize: '11px', color: '#aaa' }}>{r.requesterEmail}</div>
                </td>
                <td style={s.td}>{r.deadline || '—'}</td>
                <td style={s.td}><span style={{ ...s.badge(r.status), cursor: decisionTitle(r) ? 'help' : undefined }} title={decisionTitle(r)}>{r.status}</span></td>
                <td style={s.td} onClick={ev => ev.stopPropagation()}>{new Date(r.createdAt).toLocaleDateString()}</td>
                <td style={s.td} onClick={ev => ev.stopPropagation()}>
                  <button style={s.actionBtn} onClick={() => setMatSelected(r)}>View</button>
                  {r.planeIssueUrl && <a href={r.planeIssueUrl} target="_blank" rel="noreferrer" style={s.actionBtn}>Plane</a>}
                </td>
              </tr>
            )}
          />
        </>
      )}

      {/* Workshop registrations tab */}
      {tab === 'workshops' && (
        <>
          <div style={s.toolbar}>
            {['All', 'Pending', 'Confirmed', 'Declined', 'Cancelled'].map(f => (
              <button key={f} style={s.filterBtn(wsFilter === f)} onClick={() => { setWsFilter(f); setWsPage(1); fetchWsRegs(token, 1, f, wsSearch); }}>{f}</button>
            ))}
            <input style={s.searchInput} placeholder="Search by email…" value={wsSearch}
              onChange={e => { setWsSearch(e.target.value); setWsPage(1); fetchWsRegs(token, 1, wsFilter, e.target.value); }} />
          </div>
          <RequestTable
            columns={['Workshop', 'Registrant', 'Email', 'Role', 'Phone', 'Status', 'Registered', 'Actions']}
            rows={wsRegs} loading={wsLoading} page={wsPage} pagination={wsPagination}
            onPage={p => { setWsPage(p); fetchWsRegs(token, p, wsFilter, wsSearch); }}
            renderRow={r => (
              <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => setWsSelected(r)}>
                <td style={s.td}><span style={{ fontWeight: '600' }}>{r.workshopTitle || r.workshopId?.title || '—'}</span></td>
                <td style={s.td}>{r.fullName}</td>
                <td style={s.td}>{r.email}</td>
                <td style={s.td}>{r.role || '—'}</td>
                <td style={s.td}>{r.phone || '—'}</td>
                <td style={s.td}><span style={s.badge(r.status)}>{r.status}</span></td>
                <td style={s.td} onClick={ev => ev.stopPropagation()}>{new Date(r.createdAt).toLocaleDateString()}</td>
                <td style={{ ...s.td, minWidth: '270px', whiteSpace: 'nowrap' }} onClick={ev => ev.stopPropagation()}>
                  {r.status === 'Pending' && isWorkshopExpired(r.workshopId) && (
                    <span style={{ fontSize: '12px', color: '#999' }}>Event expired</span>
                  )}
                  {r.status === 'Pending' && !isWorkshopExpired(r.workshopId) && (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                      <DecisionButton color="#2f9e44" hoverBg="#2f9e44" onClick={async () => {
                        if (!window.confirm(`Confirm ${r.fullName}'s registration?\n\nThis emails ${r.email} their confirmation.`)) return;
                        try {
                          const res = await fetch(`${API_URL}api/workshops/admin/registrations/${r._id}/confirm`, {
                            method: 'PATCH', headers: { Authorization: `Bearer ${token}` },
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error);
                          setWsRegs(prev => prev.map(x => x._id === r._id ? { ...x, ...pickDecision(data.registration) } : x));
                        } catch (err) { alert('Failed: ' + err.message); }
                      }}><CheckCircle2 size={13} /> Confirm</DecisionButton>
                      <DecisionButton color="#c92a2a" hoverBg="#c92a2a" onClick={async () => {
                        if (!window.confirm(`Decline ${r.fullName}'s registration (${r.email})?`)) return;
                        try {
                          const res = await fetch(`${API_URL}api/workshops/admin/registrations/${r._id}/decline`, {
                            method: 'PATCH', headers: { Authorization: `Bearer ${token}` },
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error);
                          setWsRegs(prev => prev.map(x => x._id === r._id ? { ...x, ...pickDecision(data.registration) } : x));
                        } catch (err) { alert('Failed: ' + err.message); }
                      }}><XCircle size={13} /> Decline</DecisionButton>
                    </div>
                  )}
                  {(r.status === 'Confirmed' || r.status === 'Declined') && (
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '16px' }}>
                      {r.status === 'Confirmed' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#2f9e44', whiteSpace: 'nowrap' }}><CheckCircle2 size={13} /> Email sent</span>}
                      <MoreActionsMenu status={r.status} onRevert={async () => {
                        const toConfirmed = r.status === 'Declined';
                        const msg = toConfirmed
                          ? `Revert ${r.fullName} to CONFIRMED?\n\nThis emails ${r.email} an apology for the earlier denial along with their confirmation.`
                          : `Revert ${r.fullName} to DECLINED?\n\nThis emails ${r.email} an apology for the earlier acceptance and informs them the registration is declined.`;
                        if (!window.confirm(msg)) return;
                        try {
                          const res = await fetch(`${API_URL}api/workshops/admin/registrations/${r._id}/revert`, {
                            method: 'PATCH', headers: { Authorization: `Bearer ${token}` },
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error);
                          setWsRegs(prev => prev.map(x => x._id === r._id ? { ...x, ...pickDecision(data.registration) } : x));
                        } catch (err) { alert('Failed: ' + err.message); }
                      }} />
                    </div>
                  )}
                </td>
              </tr>
            )}
          />
        </>
      )}

            {/* Bookings tab */}
      {tab === 'bookings' && (
        <>
          <div style={s.toolbar}>
            {['All', 'Confirmed', 'Completed', 'Cancelled'].map(f => (
              <button key={f} style={s.filterBtn(bookFilter === f)} onClick={() => { setBookFilter(f); setBookPage(1); fetchBookings(token, 1, f); }}>{f}</button>
            ))}
          </div>
          <RequestTable
            columns={['Topic', 'Team', 'Requester', 'Date & Time', 'Duration', 'Status', 'Actions']}
            rows={bookings} loading={bookLoading} page={bookPage} pagination={bookPagination}
            onPage={p => { setBookPage(p); fetchBookings(token, p, bookFilter); }}
            renderRow={b => (
              <tr key={b._id} style={{ cursor: 'pointer' }} onClick={() => setBookSelected(b)}>
                <td style={s.td}><span style={{ fontWeight: '600' }}>{b.topic}</span></td>
                <td style={s.td}>{b.teamLabel || b.team}</td>
                <td style={s.td}>
                  <div>{b.requesterName || '—'}</div>
                  <div style={{ fontSize: '11px', color: '#aaa' }}>{b.requesterEmail}</div>
                </td>
                <td style={s.td}>{new Date(b.preferredTime).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                <td style={s.td}>{b.durationMinutes || 30} min</td>
                <td style={s.td}><span style={s.badge(b.status)}>{b.status}</span></td>
                <td style={s.td} onClick={ev => ev.stopPropagation()}>
                  {b.status !== 'Cancelled' && b.status !== 'Completed' && (
                    <button style={{ ...s.actionBtn, color: '#c92a2a' }} onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}api/booking/${b._id}/status`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ status: 'Cancelled' }),
                        });
                        if (!res.ok) throw new Error('Failed');
                        setBookings(prev => prev.map(x => x._id === b._id ? { ...x, status: 'Cancelled' } : x));
                      } catch (err) { alert('Failed: ' + err.message); }
                    }}>Cancel</button>
                  )}
                  {b.joinUrl && (
                    <a href={b.joinUrl} target="_blank" rel="noreferrer" style={{ ...s.actionBtn, textDecoration: 'none' }}>Join</a>
                  )}
                </td>
              </tr>
            )}
          />
        </>
      )}

            {/* Logs tab */}
      {tab === 'logs' && (
        <>
          <div style={s.toolbar}>
            <select style={{ ...s.searchInput, width: 'auto', marginLeft: 0 }} value={logRole}
              onChange={e => { setLogRole(e.target.value); setLogPage(1); fetchLogs(token, 1, logSearch, logType, e.target.value); }}>
              <option value=''>All Roles</option>
              <option value='user'>User</option>
              <option value='admin'>Admin</option>
            </select>
            <select style={{ ...s.searchInput, width: 'auto', marginLeft: 0 }} value={logType}
              onChange={e => { setLogType(e.target.value); setLogPage(1); fetchLogs(token, 1, logSearch, e.target.value, logRole); }}>
              <option value=''>All Types</option>
              <option value='engagement'>Engagement</option>
              <option value='booking'>Booking</option>
              <option value='material'>Material</option>
              <option value='material_request'>Material Request</option>
              <option value='workshop'>Workshop</option>
              <option value='workshop_registration'>Workshop Registration</option>
            </select>
            <input style={s.searchInput} placeholder="Search by email…" value={logSearch}
              onChange={e => { setLogSearch(e.target.value); setLogPage(1); fetchLogs(token, 1, e.target.value, logType, logRole); }} />
          </div>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Time', 'Actor', 'Role', 'Action', 'Resource', 'Detail'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logsLoading ? (
                  <tr><td colSpan={6} style={s.loading}>Loading…</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={6} style={s.emptyRow}>No logs found.</td></tr>
                ) : logs.map(log => (
                  <tr key={log._id}>
                    <td style={{ ...s.td, whiteSpace: 'nowrap', fontSize: '11px', color: '#888' }}>
                      {new Date(log.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={s.td}>
                      {log.actorName && log.actorName !== log.actorEmail && (
                        <div style={{ fontSize: '12px', fontWeight: '500' }}>{log.actorName}</div>
                      )}
                      <div style={{ fontSize: '11px', color: log.actorName && log.actorName !== log.actorEmail ? '#aaa' : '#333' }}>{log.actorEmail || '—'}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                        background: log.actorRole === 'admin' ? '#fff0f6' : '#f0f4ff',
                        color: log.actorRole === 'admin' ? '#c2255c' : '#1971c2',
                      }}>{log.actorRole}</span>
                    </td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '11px', color: '#444' }}>
                      {log.action}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontSize: '12px' }}>{log.resourceLabel || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#aaa' }}>{log.resourceType}</div>
                    </td>
                    <td style={{ ...s.td, fontSize: '11px', color: '#777', maxWidth: '200px' }}>
                      {log.metadata && Object.keys(log.metadata).length > 0
                        ? Object.entries(log.metadata).map(([k, v]) => `${k}: ${v}`).join(' · ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={s.pagination}>
              <span>{logPagination.total === 0 ? 'No results' : `Showing ${(logPage - 1) * 30 + 1}–${Math.min(logPage * 30, logPagination.total)} of ${logPagination.total}`}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={s.pageBtn(logPage <= 1)} disabled={logPage <= 1} onClick={() => { setLogPage(p => p - 1); fetchLogs(token, logPage - 1, logSearch, logType, logRole); }}><ArrowLeft size={12} /> Prev</button>
                <button style={s.pageBtn(logPage >= logPagination.pages)} disabled={logPage >= logPagination.pages} onClick={() => { setLogPage(p => p + 1); fetchLogs(token, logPage + 1, logSearch, logType, logRole); }}>Next <ArrowRight size={12} /></button>
              </div>
            </div>
          </div>
        </>
      )}

            {engSelected && (
        <EngagementModal item={engSelected} token={token} onClose={() => setEngSelected(null)}
          onStatusChange={(id, st) => setEngagements(prev => prev.map(e => e._id === id ? { ...e, status: st } : e))} />
      )}
      {matSelected && (
        <MaterialRequestModal item={matSelected} token={token} onClose={() => setMatSelected(null)}
          onStatusChange={(id, st) => setMatRequests(prev => prev.map(r => r._id === id ? { ...r, status: st } : r))}
          onFulfilled={(id, material) => setMatRequests(prev => prev.map(r => r._id === id ? { ...r, status: 'Completed', fulfilledMaterialId: material } : r))} />
      )}
    </PageShell>
  );
}